import { NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  LayoutDashboard,
  Users,
  Package,
  ShoppingCart,
  Wallet,
  Crown,
  Gift,
  LogOut,
  BellRing,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const nav = [
  { to: "/", label: "Tableau de bord", icon: LayoutDashboard, end: true, id: "dashboard" },
  { to: "/clients", label: "Clients", icon: Users, id: "clients" },
  { to: "/produits", label: "Produits", icon: Package, id: "products" },
  { to: "/commandes", label: "Commandes", icon: ShoppingCart, id: "orders" },
  { to: "/club", label: "Club Privilège", icon: Gift, id: "club" },
  { to: "/comptabilite", label: "Comptabilité", icon: Wallet, id: "accounting" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState(0);

  useEffect(() => {
    api.get("/donna/reminders").then((r) => setAlerts(r.data.length)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen flex bg-[#f8fafc]">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 border-r border-slate-200 bg-white flex flex-col fixed h-screen">
        <div className="px-6 py-6 border-b border-slate-100">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-lg bg-[#0ea5e9] flex items-center justify-center">
              <Crown className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="font-display font-bold text-[15px] leading-tight text-slate-900">
                Vert Distribution
              </p>
              <p className="text-[11px] uppercase tracking-[0.18em] text-slate-400 font-semibold">
                Espace Privilège
              </p>
            </div>
          </div>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`nav-${n.id}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200 ${
                  isActive
                    ? "bg-sky-50 text-sky-700"
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                }`
              }
            >
              <n.icon className="h-[18px] w-[18px]" />
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="px-3 py-4 border-t border-slate-100">
          <div className="px-3 py-2 mb-2">
            <p className="text-sm font-medium text-slate-800 truncate">{user?.name}</p>
            <p className="text-xs text-slate-400 truncate">{user?.email}</p>
          </div>
          <button
            data-testid="logout-button"
            onClick={logout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-600 hover:bg-red-50 hover:text-red-600 transition-colors duration-200"
          >
            <LogOut className="h-[18px] w-[18px]" />
            Déconnexion
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 ml-64 flex flex-col min-h-screen">
        <header className="h-16 sticky top-0 z-20 bg-white/80 backdrop-blur-md border-b border-slate-200 flex items-center justify-end px-8 gap-4">
          <button
            data-testid="donna-alert-badge"
            onClick={() => navigate("/clients")}
            className="relative flex items-center gap-2 px-3.5 py-2 rounded-full bg-emerald-50 text-emerald-700 text-sm font-medium hover:bg-emerald-100 transition-colors duration-200"
          >
            <BellRing className="h-4 w-4" />
            Donna
            {alerts > 0 && (
              <span className="absolute -top-1 -right-1 h-5 min-w-5 px-1 rounded-full bg-red-500 text-white text-[11px] font-bold flex items-center justify-center">
                {alerts}
              </span>
            )}
          </button>
        </header>
        <main className="flex-1 px-8 py-8">{children}</main>
      </div>
    </div>
  );
}
